import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSupportTickets } from '@/hooks/use-support-tickets';
import { useSystemIds } from '@/hooks/use-system-ids';
import { CreateTicketModal } from '@/components/support/CreateTicketModal';
import { CompanyTicketDetailModal } from '@/components/support/CompanyTicketDetailModal';
import { FAQList } from '@/components/support/FAQList';
import { ManualList } from '@/components/support/ManualList';
import { ManualABNTInline } from '@/components/support/ManualABNTInline';
import { ManualABNTTOC } from '@/components/support/ManualABNTTOC';
import { fleetoneABNTManual } from '@/data/manuals/fleetone-abnt-content';
import { utmGuideManual } from '@/data/manuals/utm-guide-content';
import { trackingoneManual } from '@/data/manuals/trackingone-content';
import type { ManualABNTData } from '@/data/manuals/fleetone-abnt-content';
import { Search, Plus, TicketIcon, HelpCircle, FileText, BookOpen, MoreHorizontal } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { ManualContent } from '@/components/support/ManualContent';
import { htmlToMarkdown, parseMarkdownTOC, TOCItem } from '@/lib/markdown-utils';
import { useSupportManuals } from '@/hooks/use-support-manuals';
import { ManualTOC } from '@/components/support/ManualTOC';
// Force rebuild
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CardDescription } from '@/components/ui/card';

interface SupportPageProps {
  companyId: string;
}

export default function SupportPage({ companyId }: SupportPageProps) {
  const { user } = useAuth();
  const { data: tickets, isLoading: loadingTickets } = useSupportTickets(companyId);
  const [ticketFilter, setTicketFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const { data: systemIds } = useSystemIds();
  const [selectedManual, setSelectedManual] = useState<any>(null);
  const [manualFilter, setManualFilter] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [activeAnchor, setActiveAnchor] = useState<string>('');
  const { data: allManuals } = useSupportManuals();
  const manualContentRef = useRef<HTMLDivElement>(null);
  const [selectedManualType, setSelectedManualType] = useState<'standard' | 'abnt' | null>(null);
  const [selectedABNTManual, setSelectedABNTManual] = useState<ManualABNTData | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate TOC when standard manual is selected
  useEffect(() => {
    if (selectedManual && selectedManualType === 'standard') {
      const markdown = htmlToMarkdown(selectedManual.content);
      const toc = parseMarkdownTOC(markdown);
      setTocItems(toc);
      setActiveAnchor('');
    } else if (selectedManualType !== 'abnt') {
      setTocItems([]);
      setActiveAnchor('');
    }
  }, [selectedManual, selectedManualType]);

  const filteredManuals = allManuals?.filter(manual => 
    manual.title.toLowerCase().includes(manualFilter.toLowerCase()) ||
    manual.category.toLowerCase().includes(manualFilter.toLowerCase())
  ) || [];

  console.log('👤 Current user:', user?.id);
  console.log('🏢 Company ID:', companyId);
  console.log('🎫 All tickets:', tickets?.length || 0);

  const userTickets = tickets?.filter(ticket => {
    console.log('🔍 Checking ticket:', ticket.id, 'user_id:', ticket.user_id, 'current user:', user?.id);
    return ticket.user_id === user?.id;
  });

  console.log('👤 User tickets:', userTickets?.length || 0);

  const filteredTickets = userTickets?.filter(ticket => {
    const matchesFilter = ticketFilter === '' || 
      ticket.title.toLowerCase().includes(ticketFilter.toLowerCase()) ||
      ticket.description.toLowerCase().includes(ticketFilter.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    
    return matchesFilter && matchesStatus;
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'open': return 'destructive';
      case 'in_progress': return 'default';
      case 'resolved': return 'secondary';
      case 'closed': return 'outline';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Aberto';
      case 'in_progress': return 'Em Andamento';
      case 'resolved': return 'Resolvido';
      case 'closed': return 'Fechado';
      default: return status;
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return priority;
    }
  };

  const openTicketsCount = userTickets?.filter(t => t.status === 'open').length || 0;
  const inProgressTicketsCount = userTickets?.filter(t => t.status === 'in_progress').length || 0;
  const resolvedTicketsCount = userTickets?.filter(t => t.status === 'resolved').length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Central de Suporte</h1>
          <p className="text-muted-foreground">
            Abra chamados, consulte FAQs e acesse manuais
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Chamados Abertos</CardTitle>
            <TicketIcon className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{openTicketsCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
            <TicketIcon className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{inProgressTicketsCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolvidos</CardTitle>
            <TicketIcon className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{resolvedTicketsCount}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="tickets" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tickets">Meus Chamados</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="manuals">Manuais</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Meus Chamados</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar chamados..."
                      className="pl-8 w-64"
                      value={ticketFilter}
                      onChange={(e) => setTicketFilter(e.target.value)}
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      <SelectItem value="open">Abertos</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="resolved">Resolvidos</SelectItem>
                      <SelectItem value="closed">Fechados</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setShowCreateTicket(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Chamado
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Sistema</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingTickets ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Carregando seus chamados...
                      </TableCell>
                    </TableRow>
                  ) : filteredTickets?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <TicketIcon className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">
                            {ticketFilter || statusFilter !== 'all' 
                              ? 'Nenhum chamado encontrado com os filtros aplicados'
                              : 'Você ainda não criou nenhum chamado'
                            }
                          </p>
                          {!ticketFilter && statusFilter === 'all' && (
                            <Button onClick={() => setShowCreateTicket(true)}>
                              <Plus className="h-4 w-4 mr-2" />
                              Criar Primeiro Chamado
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets?.map((ticket) => (
                      <TableRow 
                        key={ticket.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedTicketId(ticket.id)}
                      >
                        <TableCell className="font-medium">{ticket.title}</TableCell>
                        <TableCell>{(ticket.category as any)?.name || 'Sem categoria'}</TableCell>
                        <TableCell>
                          {(ticket.system as any)?.name || <span className="text-muted-foreground">Geral</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                            {getPriorityLabel(ticket.priority)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(ticket.status)}>
                            {getStatusLabel(ticket.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="faqs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Perguntas Frequentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FAQList />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manuals" className="space-y-4">
          {isMobile ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Manuais e Documentação
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="list">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="list">Lista</TabsTrigger>
            <TabsTrigger value="toc" disabled={!selectedManual}>Índice</TabsTrigger>
            <TabsTrigger value="content" disabled={!selectedManual}>
              Conteúdo
            </TabsTrigger>
          </TabsList>
                  
                  <TabsContent value="list" className="space-y-3">
                    <Input
                      placeholder="Buscar manuais..."
                      value={manualFilter}
                      onChange={(e) => setManualFilter(e.target.value)}
                      className="mb-3"
                    />
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                      {filteredManuals.map(manual => (
                        <button
                          key={manual.id}
                          onClick={() => setSelectedManual(manual)}
                          className={cn(
                            "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                            selectedManual?.id === manual.id && "bg-muted ring-1 ring-primary"
                          )}
                        >
                          <p className="font-medium text-sm line-clamp-2">{manual.title}</p>
                          <div className="flex gap-2 mt-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{manual.category}</Badge>
                            {manual.systems && (
                              <Badge variant="outline" className="text-xs">{manual.systems.name}</Badge>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
          </TabsContent>
          
          <TabsContent value="toc">
            {selectedManual && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Índice</CardTitle>
                </CardHeader>
                <CardContent>
                  <ManualTOC
                    items={tocItems}
                    activeAnchor={activeAnchor}
                    onItemClick={(anchor) => {
                      setActiveAnchor(anchor);
                      const tabTrigger = document.querySelector('[value="content"]') as HTMLElement;
                      tabTrigger?.click();
                      setTimeout(() => {
                        const element = document.getElementById(anchor);
                        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="content">
                    {selectedManual && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="font-semibold text-lg">{selectedManual.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">{selectedManual.category}</Badge>
                            {selectedManual.systems && (
                              <Badge variant="outline" className="text-xs">{selectedManual.systems.name}</Badge>
                            )}
                          </div>
                        </div>
                <ManualContent 
                  content={htmlToMarkdown(selectedManual.content)} 
                  manualId={selectedManual.id}
                  className="max-h-[60vh] overflow-y-auto"
                  onScrollSpy={setActiveAnchor}
                />
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-[280px_320px_1fr] gap-4 h-[calc(100vh-280px)] min-h-0">
              <Card className="flex flex-col h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Manuais</CardTitle>
                  <div className="pt-2">
                    <Input
                      placeholder="Buscar..."
                      value={manualFilter}
                      onChange={(e) => setManualFilter(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 overflow-y-auto p-3 custom-scrollbar">
                  <div className="space-y-2">
                    {/* Manual ABNT do FleetONE */}
                    <button
                      onClick={() => {
                        setSelectedManualType('abnt');
                        setSelectedABNTManual(fleetoneABNTManual);
                        setSelectedManual(null);
                      }}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                        selectedManualType === 'abnt' && selectedABNTManual === fleetoneABNTManual && "bg-muted ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Manual FleetONE</p>
                          <p className="text-xs text-muted-foreground">Gestão de Frota</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">ABNT</Badge>
                        <Badge variant="outline" className="text-xs">v{fleetoneABNTManual.metadata.version}</Badge>
                      </div>
                    </button>

                    {/* Manual ABNT do Guia UTM */}
                    <button
                      onClick={() => {
                        setSelectedManualType('abnt');
                        setSelectedABNTManual(utmGuideManual);
                        setSelectedManual(null);
                      }}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                        selectedManualType === 'abnt' && selectedABNTManual === utmGuideManual && "bg-muted ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Guia UTM</p>
                          <p className="text-xs text-muted-foreground">Rastreamento de Campanhas</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">Marketing</Badge>
                        <Badge variant="outline" className="text-xs">v{utmGuideManual.metadata.version}</Badge>
                      </div>
                    </button>

                    {/* Manual ABNT do TrackingONE */}
                    <button
                      onClick={() => {
                        setSelectedManualType('abnt');
                        setSelectedABNTManual(trackingoneManual);
                        setSelectedManual(null);
                      }}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                        selectedManualType === 'abnt' && selectedABNTManual === trackingoneManual && "bg-muted ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Manual TrackingONE</p>
                          <p className="text-xs text-muted-foreground">Rastreamento GPS</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">ABNT</Badge>
                        <Badge variant="outline" className="text-xs">v{trackingoneManual.metadata.version}</Badge>
                      </div>
                    </button>

                    {/* Separador */}
                    {filteredManuals.length > 0 && (
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Outros Manuais
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Manuais padrão existentes */}
                    {filteredManuals.map(manual => (
                      <button
                        key={manual.id}
                        onClick={() => {
                          setSelectedManualType('standard');
                          setSelectedManual(manual);
                          setSelectedABNTManual(null);
                        }}
                        className={cn(
                          "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                          selectedManualType === 'standard' && selectedManual?.id === manual.id && "bg-muted ring-1 ring-primary"
                        )}
                      >
                        <p className="font-medium text-sm line-clamp-2">{manual.title}</p>
                        <div className="flex gap-2 mt-1.5 flex-wrap">
                          <Badge variant="secondary" className="text-xs">{manual.category}</Badge>
                          {manual.systems && (
                            <Badge variant="outline" className="text-xs">{manual.systems.name}</Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Coluna do Índice/TOC (200px) */}
              <Card className="flex flex-col h-full min-h-0">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-sm font-semibold">Índice</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 overflow-y-auto p-3 custom-scrollbar">
                  {selectedManualType === 'abnt' && selectedABNTManual ? (
                    <ManualABNTTOC
                      sections={selectedABNTManual.body.sections}
                      activeSection={activeSection}
                    onSectionClick={(sectionNumber) => {
                      const element = document.getElementById(`section-${sectionNumber}`);
                      const container = document.getElementById('manual-abnt-inline-container');
                      if (container && element) {
                        const top = element.offsetTop - container.offsetTop - 16;
                        container.scrollTo({ top, behavior: 'smooth' });
                      }
                    }}
                    />
                  ) : selectedManualType === 'standard' && selectedManual && tocItems.length > 0 ? (
                    <ManualTOC
                      items={tocItems}
                      activeAnchor={activeAnchor}
                      onItemClick={(anchor) => {
                        setActiveAnchor(anchor);
                        const container = manualContentRef.current;
                        const element = document.getElementById(anchor);
                        
                        if (container && element) {
                          const scrollTop = element.offsetTop - container.offsetTop - 20;
                          container.scrollTo({
                            top: scrollTop,
                            behavior: 'smooth'
                          });
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-xs text-muted-foreground text-center">
                        Selecione um manual
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Coluna Direita - Conteúdo do Manual */}
              <Card className="flex flex-col h-full min-h-0">
                {selectedManualType === 'abnt' && selectedABNTManual ? (
                  <ManualABNTInline
                    manualData={selectedABNTManual}
                    onScrollSpy={setActiveSection}
                  />
                ) : selectedManualType === 'standard' && selectedManual ? (
                  <>
                    <CardHeader className="border-b pb-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{selectedManual.title}</CardTitle>
                          <CardDescription className="mt-1">
                            <div className="flex items-center gap-2 text-xs">
                              <Badge variant="secondary">{selectedManual.category}</Badge>
                              {selectedManual.systems && (
                                <Badge variant="outline">{selectedManual.systems.name}</Badge>
                              )}
                              {selectedManual.updated_at && (
                                <span className="text-muted-foreground">
                                  • Atualizado em {format(new Date(selectedManual.updated_at), "dd/MM/yyyy")}
                                </span>
                              )}
                            </div>
                          </CardDescription>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => window.print()}>
                          Imprimir
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent ref={manualContentRef} id="manual-scroll-container" className="flex-1 min-h-0 overflow-y-auto p-6 custom-scrollbar">
                      <ManualContent
                        content={htmlToMarkdown(selectedManual.content)} 
                        manualId={selectedManual.id}
                        onScrollSpy={setActiveAnchor}
                      />
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Selecione um manual para visualizar
                      </p>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <CreateTicketModal
        open={showCreateTicket}
        onOpenChange={setShowCreateTicket}
        companyId={companyId}
      />
      
      {selectedTicketId && (
        <CompanyTicketDetailModal
          ticketId={selectedTicketId}
          open={!!selectedTicketId}
          onOpenChange={(open) => !open && setSelectedTicketId(null)}
        />
      )}
    </div>
  );
}